"use client";

import { type ReactNode, useState, useRef, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuth, useUser } from "./context";

// -----------------------------------------------------------------------------
// SignedIn / SignedOut - Conditional rendering based on auth state
// -----------------------------------------------------------------------------

interface ConditionalProps {
  children: ReactNode;
}

export function SignedIn({ children }: ConditionalProps) {
  const { isLoaded, isSignedIn } = useAuth();
  // Don't render until loaded AND signed in
  if (!isLoaded || !isSignedIn) return null;
  return <>{children}</>;
}

export function SignedOut({ children }: ConditionalProps) {
  const { isLoaded, isSignedIn } = useAuth();
  // Show signed-out content while loading (better UX - avoids blank state)
  // Hide only when we KNOW user is signed in
  if (isLoaded && isSignedIn) return null;
  return <>{children}</>;
}

// -----------------------------------------------------------------------------
// SignInButton / SignUpButton - Navigate to auth pages
// -----------------------------------------------------------------------------

interface AuthButtonProps {
  children?: ReactNode;
  mode?: "redirect" | "modal";
  redirectUrl?: string;
  className?: string;
  onClick?: () => void;
}

export function SignInButton({ children, mode: _mode = "redirect", redirectUrl, className, onClick }: AuthButtonProps) {
  const router = useRouter();

  const handleClick = () => {
    onClick?.();
    const url = redirectUrl ? `/sign-in?redirect_url=${encodeURIComponent(redirectUrl)}` : "/sign-in";
    router.push(url);
  };

  return (
    <button type="button" onClick={handleClick} className={className}>
      {children || "Sign in"}
    </button>
  );
}

export function SignUpButton({ children, mode: _mode = "redirect", redirectUrl, className, onClick }: AuthButtonProps) {
  const router = useRouter();

  const handleClick = () => {
    onClick?.();
    const url = redirectUrl ? `/sign-up?redirect_url=${encodeURIComponent(redirectUrl)}` : "/sign-up";
    router.push(url);
  };

  return (
    <button type="button" onClick={handleClick} className={className}>
      {children || "Sign up"}
    </button>
  );
}

// -----------------------------------------------------------------------------
// UserButton - Dropdown with user info and sign out
// -----------------------------------------------------------------------------

interface UserButtonProps {
  afterSignOutUrl?: string;
  appearance?: {
    elements?: {
      avatarBox?: string;
      userButtonPopoverCard?: string;
    };
  };
}

export function UserButton({ afterSignOutUrl = "/" }: UserButtonProps) {
  const { user, isLoaded } = useUser();
  const { signOut } = useAuth();
  const _router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isLoaded || !user) return null;

  const initials = (() => {
    if (user.fullName) {
      const words = user.fullName.trim().split(/\s+/).filter(Boolean);
      if (words.length === 1) {
        return words[0][0].toUpperCase();
      }
      // First initial + last initial
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return user.email?.slice(0, 2).toUpperCase() || "??";
  })();

  const handleSignOut = async () => {
    setIsOpen(false);
    await signOut();
    // Use full page redirect to ensure server sees the cleared session
    window.location.href = afterSignOutUrl;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
        aria-label="User menu"
      >
        {user.imageUrl ? (
          <img src={user.imageUrl} alt={user.fullName || "User"} className="h-8 w-8 rounded-full object-cover" />
        ) : (
          initials
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900 truncate">{user.fullName || "User"}</p>
            <p className="text-xs text-gray-500 truncate">{user.email}</p>
          </div>

          <div className="py-1">
            <Link
              href="/dashboard"
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => setIsOpen(false)}
            >
              Dashboard
            </Link>
            <Link
              href="/settings"
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => setIsOpen(false)}
            >
              Settings
            </Link>
          </div>

          <div className="border-t border-gray-100 py-1">
            <button
              type="button"
              onClick={handleSignOut}
              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Placeholder components for compatibility
// These components exist in Clerk but we don't need them for Supabase Auth
// -----------------------------------------------------------------------------

export function SignIn() {
  return null; // Handled by sign-in page
}

export function SignUp() {
  return null; // Handled by sign-up page
}
